import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Building2, Users, Award, ArrowRight, X } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { mockCoachingTieUps, mockPartnerLeads, type CoachingTieUp } from '@/data/admin-mock-data'

export default function PartnerInstitutesPage() {
    const [enrollModalOpen, setEnrollModalOpen] = useState(false)
    const [selectedInstitute, setSelectedInstitute] = useState<CoachingTieUp | null>(null)
    const [enrollForm, setEnrollForm] = useState({ name: '', email: '', phone: '', courseInterest: '' })

    const institutes = mockCoachingTieUps.filter(i => i.isActive)

    const openEnroll = (institute: CoachingTieUp) => {
        setSelectedInstitute(institute)
        setEnrollModalOpen(true)
    }

    const handleEnrollSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!selectedInstitute) return

        const newLead = {
            id: `pl-${Date.now()}`,
            studentName: enrollForm.name,
            email: enrollForm.email,
            phone: enrollForm.phone,
            instituteId: selectedInstitute.id,
            instituteName: selectedInstitute.name,
            status: 'new',
            assignedMentorId: null,
            assignedMentorName: null,
            courseInterest: enrollForm.courseInterest,
            createdAt: new Date().toISOString().split('T')[0]
        }

        // @ts-ignore
        mockPartnerLeads.push(newLead)

        alert('Application submitted successfully! Our counsellor will contact you shortly.')
        setEnrollModalOpen(false)
        setEnrollForm({ name: '', email: '', phone: '', courseInterest: '' })
    }

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-32 pb-16 overflow-hidden">
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-6">
                            Partner Institutes
                        </span>
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6">
                            <span className="text-theme-primary">India's Top</span>
                            <span className="block mt-2 gradient-text">Coaching Institutes</span>
                        </h1>
                        <p className="text-xl text-theme-secondary leading-relaxed mb-8">
                            Join the best coaching centers in the country through Future Founder
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Institute Showcase */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {institutes.map((institute, index) => (
                            <motion.div
                                key={institute.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <GlassmorphicCard className="overflow-hidden hover:scale-105 transition-all duration-300 h-full flex flex-col">
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src={institute.bannerUrl || institute.image}
                                            alt={institute.name}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute top-3 left-3">
                                            <div className="w-10 h-10 rounded-full bg-white p-1 shadow-lg">
                                                <img src={institute.image} alt="logo" className="w-full h-full object-contain rounded-full" />
                                            </div>
                                        </div>
                                        <div className="absolute top-3 right-3">
                                            <span className="px-3 py-1 bg-accent-gold text-slate-900 text-sm font-bold rounded-full flex items-center gap-1">
                                                <Award className="h-3 w-3" />
                                                {institute.rating}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <h3 className="text-xl font-display font-semibold text-theme-primary mb-2">
                                            {institute.name}
                                        </h3>
                                        <p className="text-primary-blue text-sm mb-4 flex items-center gap-1">
                                            <Building2 className="h-4 w-4" />
                                            {institute.location}
                                        </p>

                                        <div className="space-y-2 mb-4 text-sm text-theme-secondary">
                                            {institute.courses && (
                                                <p><strong>Courses:</strong> {institute.courses}</p>
                                            )}
                                            {institute.feesRange && (
                                                <p><strong>Fees:</strong> {institute.feesRange}</p>
                                            )}
                                        </div>

                                        <div className="mt-auto">
                                            <div className="flex items-center gap-2 text-theme-muted mb-4 text-sm">
                                                <Users className="h-4 w-4" />
                                                <span>{institute.students} students enrolled</span>
                                            </div>
                                            <button onClick={() => openEnroll(institute)} className="w-full px-4 py-3 bg-primary-blue hover:bg-blue-600 text-white font-medium rounded-lg transition-all duration-300 inline-flex items-center justify-center gap-2">
                                                Join Coaching Now
                                                <ArrowRight className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </GlassmorphicCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Enroll Modal */}
            <AnimatePresence>
                {enrollModalOpen && selectedInstitute && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setEnrollModalOpen(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="w-full max-w-md"
                            onClick={e => e.stopPropagation()}
                        >
                            <GlassmorphicCard className="p-6 relative">
                                <button onClick={() => setEnrollModalOpen(false)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                    <X className="h-5 w-5 text-theme-secondary" />
                                </button>

                                <h3 className="text-2xl font-bold text-theme-primary mb-2">Enroll at {selectedInstitute.name}</h3>
                                <p className="text-theme-secondary mb-6 text-sm">
                                    Submit your details to get a callback from our admission team.
                                </p>

                                <form onSubmit={handleEnrollSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Full Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={enrollForm.name}
                                            onChange={e => setEnrollForm(f => ({ ...f, name: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                            placeholder="Enter your name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Email Address</label>
                                        <input
                                            type="email"
                                            required
                                            value={enrollForm.email}
                                            onChange={e => setEnrollForm(f => ({ ...f, email: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                            placeholder="Enter your email"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Phone Number</label>
                                        <input
                                            type="tel"
                                            required
                                            value={enrollForm.phone}
                                            onChange={e => setEnrollForm(f => ({ ...f, phone: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                            placeholder="Enter your phone"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Course Interest</label>
                                        <input
                                            value={enrollForm.courseInterest}
                                            onChange={e => setEnrollForm(f => ({ ...f, courseInterest: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                            placeholder="e.g. NEET 2-Year Program"
                                        />
                                    </div>
                                    <button type="submit" className="w-full py-3 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-all duration-300">
                                        Submit Application
                                    </button>
                                </form>
                            </GlassmorphicCard>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
